// OPTI-38 — 測試環境(UAT/TST) debug：在 header 顯示 token「剩餘時間倒數」。
// 來源：login-pass 2.3.0 new mode 把到期時間(epoch ms)寫進 localStorage 'tokenExpireAt'。
// 續期：有操作 → login-pass 滑動續期重寫 tokenExpireAt → 下一個 tick 自動讀到新值跳回重數。
// 只有 UAT/TST 顯示(host 含 'uat'/'tst')；PRD/天級客戶不出現。剩 < 5 分才顯示、< 1 分轉紅。
// 倒數歸 0（分鐘級 token 真的過期）→ 主動觸發 idleTimeout 登出（補站台 proactive idle 未生效時的洞）。
//   零回歸：只在 test-env + 有 tokenExpireAt（分鐘級）才動；天級無此 key → remainingMs 恆 null → 永不觸發。
//
// 針對性 eslint disable（取代原 whole-file disable，僅停用此 debug widget 真正需要的 4 條）：
//   - react/jsx-filename-extension：此檔為 .js 內含 JSX（與既有 linkup .js 一致，改 .jsx 影響 webpack 解析）。
//   - max-len：保留中文說明註解。
//   - no-empty：localStorage 例外的 catch 刻意 no-op，不可炸 header。
//   - import/no-unresolved：logout 採 linkup webpack src-absolute import（同 utils//configs/ 慣例），
//     repo .eslintrc 未配 import resolver，故與既有 .js 一致停用此規則。
/* eslint-disable react/jsx-filename-extension, max-len, no-empty, import/no-unresolved */
import React, { Component } from 'react';
import { logout } from 'containers/App/reducers/userReducer';

const THRESHOLD_MS = 5 * 60 * 1000; // 剩 5 分鐘內才顯示
const URGENT_MS = 1 * 60 * 1000; // < 1 分鐘轉紅
const EXPIRE_KEY = 'tokenExpireAt'; // login-pass 2.3.0 new mode marker

function isTestEnv() {
  try {
    if (typeof window === 'undefined') return false;
    const host = String(window.location.host || '');
    // UAT(uat-tube) 與 TST(tst-tube) 都顯示；PRD 不含這兩個字 → 不顯示。
    return host.indexOf('uat') > -1 || host.indexOf('tst') > -1;
  } catch (e) {
    return false;
  }
}

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

class TokenCountdown extends Component {
  constructor(props) {
    super(props);
    this.state = { remainingMs: null };
    this.fired = false; // 倒數歸 0 的 idle 登出只觸發一次
    this.tick = this.tick.bind(this);
  }

  componentDidMount() {
    if (!isTestEnv()) return; // 非 UAT/TST 不啟動 timer
    this.tick();
    this.timer = setInterval(this.tick, 1000);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  tick() {
    let remainingMs = null;
    try {
      const raw = window.localStorage.getItem(EXPIRE_KEY);
      if (raw) {
        const r = Number(raw) - Date.now();
        if (!Number.isNaN(r)) remainingMs = r;
      }
    } catch (e) {
      // localStorage 取不到就當沒有，不可炸 header
    }
    // 倒數歸 0（分鐘級 token 真的過期）→ 主動觸發 idleTimeout 登出，只觸發一次。
    // 天級無 tokenExpireAt → remainingMs 恆 null → 不進此分支（零回歸）。
    if (remainingMs !== null && remainingMs <= 0 && !this.fired) {
      this.fired = true;
      if (this.timer) clearInterval(this.timer);
      try {
        // logout 是 imperative redirect function（清 storage + 同步 window.location 導向），
        // 非 Redux action creator，可直接呼叫、不需 dispatch。第一參數 loginPath 傳 undefined。
        logout(undefined, 'idleTimeout');
      } catch (e) {
        // 導向失敗不可炸 header
      }
      return;
    }
    this.setState({ remainingMs });
  }

  render() {
    if (!isTestEnv()) return null;

    const { remainingMs } = this.state;
    // 只在「有到期時間、還沒過期、且剩 < 5 分」時長出來
    if (remainingMs === null || remainingMs <= 0 || remainingMs >= THRESHOLD_MS) {
      return null;
    }

    const totalSec = Math.floor(remainingMs / 1000);
    const mm = pad2(Math.floor(totalSec / 60));
    const ss = pad2(totalSec % 60);
    const urgent = remainingMs < URGENT_MS;
    const color = urgent ? '#f5222d' : '#5b6b79';

    const wrapStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      height: '32px',
      marginRight: '16px',
      padding: '0 10px',
      borderRadius: '14px',
      background: urgent ? 'rgba(245,34,45,0.10)' : 'rgba(0,0,0,0.05)',
      color,
      fontSize: '13px',
      fontFamily: 'Menlo, Consolas, monospace',
      fontVariantNumeric: 'tabular-nums',
      lineHeight: '1',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      verticalAlign: 'middle',
    };

    const iconStyle = { marginRight: '6px', flex: '0 0 auto', display: 'block' };

    return (
      <div style={wrapStyle} title="UAT/TST debug：token 剩餘時間（有操作續期後會重新倒數）">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={iconStyle}
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 1.8" />
        </svg>
        {`${mm}:${ss}`}
      </div>
    );
  }
}

export default TokenCountdown;



/** WEBPACK FOOTER **
 ** D:/a/1/s/src/components/Header/components/TokenCountdown/index.js
 **/